import React, {
  useState,
  createContext,
  ReactNode,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';
import { FieldForMapping } from './mapping-table';
import { FieldNotRegisteredInLad } from './fields-not-registered-in-lad-table';
import { CurrentStatusWithLadFieldForMapping } from './current-field-status-with-lad-table';
import Field from '../../../../../../interfaces/field';
import { FIELD_STORED, useTable } from './use-synchronize';

export interface FieldForSync extends Partial<Field> {
  area: number;
  name: string;
  block_number: string;
  field_status: string;
  lad_number: string;
  has_match?: string;
  cargo_id?: string;
  group_id?: string;
  stored?: ReactNode | keyof typeof FIELD_STORED;
}

interface MappingState {
  state: FieldForMapping[];
  checked: FieldForMapping[];
  hovered: FieldForMapping | null;
}

interface FieldSearchForMappingState {
  hovered: FieldForSync | null;
  checked: FieldForSync[];
  height: number;
}

interface FieldsNotRegisteredInLadState {
  checked: FieldNotRegisteredInLad[];
}

interface CurrentLadStatusWithLadState {
  editable: CurrentStatusWithLadFieldForMapping[];
  checked: CurrentStatusWithLadFieldForMapping[];
  hovered: CurrentStatusWithLadFieldForMapping | null;
  state: CurrentStatusWithLadFieldForMapping[];
}

interface ContextValue {
  mappingState: MappingState;
  fieldsNotRegisteredInLadState: FieldsNotRegisteredInLadState;
  currentLadStatusWithLadState: CurrentLadStatusWithLadState;
  fieldSearchForMappingState: FieldSearchForMappingState;
  setMappingState: Dispatch<SetStateAction<MappingState>>;
  setFieldsNotRegisteredInLadState: Dispatch<
    SetStateAction<FieldsNotRegisteredInLadState>
  >;
  setCurrentLadStatusWithLadState: Dispatch<
    SetStateAction<CurrentLadStatusWithLadState>
  >;
  setFieldSearchForMappingState: Dispatch<
    SetStateAction<FieldSearchForMappingState>
  >;
}

const FieldsForMappingContext = createContext<ContextValue>({
  mappingState: {
    state: [],
    checked: [],
    hovered: null,
  },
  fieldsNotRegisteredInLadState: {
    checked: [],
  },
  currentLadStatusWithLadState: {
    editable: [],
    state: [],
    hovered: null,
    checked: [],
  },
  fieldSearchForMappingState: {
    hovered: null,
    checked: [],
    height: 0,
  },
  setMappingState: () => {},
  setFieldsNotRegisteredInLadState: () => {},
  setCurrentLadStatusWithLadState: () => {},
  setFieldSearchForMappingState: () => {},
});

export const FieldsForMappingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { farmId, season, fieldService } = useTable();

  useEffect(() => {}, [fieldService, farmId, season]);

  const [mappingState, setMappingState] = useState<MappingState>({
    state: [],
    checked: [],
    hovered: null,
  });

  const [fieldsNotRegisteredInLadState, setFieldsNotRegisteredInLadState] =
    useState<FieldsNotRegisteredInLadState>({
      checked: [],
    });

  const [currentLadStatusWithLadState, setCurrentLadStatusWithLadState] =
    useState<CurrentLadStatusWithLadState>({
      editable: [],
      hovered: null,
      state: [],
      checked: [],
    });

  const [fieldSearchForMappingState, setFieldSearchForMappingState] =
    useState<FieldSearchForMappingState>({
      hovered: null,
      checked: [],
      height: 0,
    });

  return (
    <FieldsForMappingContext.Provider
      value={{
        mappingState,
        fieldsNotRegisteredInLadState,
        currentLadStatusWithLadState,
        fieldSearchForMappingState,
        setMappingState,
        setFieldsNotRegisteredInLadState,
        setCurrentLadStatusWithLadState,
        setFieldSearchForMappingState,
      }}
    >
      {children}
    </FieldsForMappingContext.Provider>
  );
};

export const useFieldsForMapping = () => useContext(FieldsForMappingContext);
